<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue";
import { useForm } from "@inertiajs/inertia-vue3";
import DialogModal from "../../DialogModal.vue";
import SecondaryButton from "../../SecondaryButton.vue";
import PrimaryButton from "../../PrimaryButton.vue";
import { Inertia } from "@inertiajs/inertia";

const props = defineProps({
  mediaInput: {
    type: Object,
    default() {
      return { 'F': [], 'I': [] };
    }
  },
  canSeeUploadForm: {
    type: Boolean,
    default() {
      return true;
    }
  },
  hideSave: {
    type: Boolean,
    default() {
      return false;
    }
  },
  sampleId: {
    type: String,
    required: true,
  },
  canSeeFiles: {
    type: Boolean,
    default() {
      return true;
    }
  },
  required: {
    type: Boolean,
    default() {
      return false;
    }
  }
});

const selectMedia = ref(null);
const form = useForm({
  media: [],
});
const uploadMedia = () => {
  form.media = selectMedia.value.files;
  form.post(route('admin.samples.uploads.store', { sample: props.sampleId }), {
    preserveScroll: true,
    onFinish() {
      form.reset();
      selectMedia.value.value = null;
    }
  })
}

const deleting = ref({ media: null, loading: false })
const openDeleteMediaModal = (media) => {
  deleting.value.media = media;
}

const closeDeletingMediaModal = () => {
  deleting.value.media = null;
  deleting.value.loading = false;
}
const removeMedia = (mediaId) => {
  deleting.value.loading = true;

  Inertia.delete(route('admin.samples.uploads.destroy', { sample: props.sampleId, ids: [mediaId] }), {
    preserveScroll: true,
    onFinish() {
      closeDeletingMediaModal();
    }
  });
}

const preview = ref(null);
const openPreviewModal = (image) => {
  preview.value = image;
}

const closePreviewModal = () => {
  preview.value = null;
}

const dropArea = ref({ active: false });
const events = ['dragenter', 'dragover', 'dragleave', 'drop']

onMounted(() => {
  events.forEach((eventName) => {
    document.body.addEventListener(eventName, preventDefaults)
  })
})

onUnmounted(() => {
  events.forEach((eventName) => {
    document.body.removeEventListener(eventName, preventDefaults)
  })
})

const dropMedia = (event) => {
  dropArea.value.active = false;
  selectMedia.value.files = event.dataTransfer.files;
  uploadMedia();
}

const preventDefaults = (e) => {
  e.preventDefault()
}

const isImageType = (media) => {
  return media.uploadtype === 'I';
}
const isFileType = (media) => {
  return media.uploadtype === 'F';
}

const fallbackToOldImage = (event) => {
  const current = event.target.src;
  if (current.includes('/sample/')) {
    event.target.src = current.replace('/sample/', '/sample_database/');
  }
};

const emit = defineEmits(['images-present']);

watch(
   () => (Array.isArray(props.mediaInput?.I) ? props.mediaInput.I.length : 0),
   (len) => {
     emit('images-present', len > 0);
   },
   { immediate: true }
 );
</script>

<template>
  <div class="px-4 py-5 bg-white sm:p-6 shadow sm:rounded-tl-md sm:rounded-tr-md mb-5">
    <div class="sm:grid grid-cols-6 gap-6" v-if="!hideSave && canSeeUploadForm">
      <div class="col-span-6">
        <div class="flex justify-center items-center w-full">
          <label for="media-input" @drop="dropMedia" @dragenter.prevent="dropArea.active = true"
            @dragover.prevent="dropArea.active = true" @dragleave.prevent="dropArea.active = false"
            class="flex flex-col justify-center items-center w-full h-32 rounded-lg border-2 border-dashed cursor-pointer hover:bg-gray-100"
            :class="dropArea.active ? ['border-gray-500', 'bg-gray-100'] : ['border-gray-300']">
            <div class="flex flex-col justify-center items-center pt-5 pb-6">
              <svg aria-hidden="true" class="mb-3 w-10 h-10 text-gray-400" fill="none" stroke="currentColor"
                viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
              </svg>
              <p class="mb-2 text-sm text-gray-500"><span class="font-semibold">Click to upload</span> or drag and drop
              </p>
              <p class="text-xs text-gray-500">Max. File Size: 30MB</p>
            </div>
            <input @change="uploadMedia()" id="media-input" ref="selectMedia" class="hidden" type="file"
              :multiple="true">
          </label>
        </div>
      </div>
    </div>
    <p class="font-bold mt-5 mb-2">Images <span v-if="required" class="text-red-600">*</span></p>
    <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3">
      <figure v-for="media in mediaInput.I" class="relative cursor-pointer min-h-32">
        <figcaption class="bg-gray-50/50 w-full">
          <div class="text-center mt-1 text-sm" v-text="media.filename"></div>
        </figcaption>
        <img :src="media.url" :alt="media.filename" class="h-auto max-w-full rounded-lg cursor-pointer"
          @click="openPreviewModal(media)" @error="event => fallbackToOldImage(event)" v-if="isImageType(media)" />
        <figcaption class="absolute bottom-2 bg-gray-50/50 w-full" v-if="!hideSave">
          <div class="text-center mt-1 text-xl justify-between flex mx-3" v-if="isImageType(media)">
            <button type="button" class="fa fa-trash text-red-500"
              @click.prevent="openDeleteMediaModal(media)"></button>
          </div>
        </figcaption>
      </figure>
      <p v-if="!mediaInput.I" class="text-gray-700">No records...</p>
    </div>
    <hr class="mt-5 mb-5 text-gray-200" v-if="canSeeFiles">
    <p class="font-bold mb-2" v-if="canSeeFiles">Files</p>
    <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-3" v-if="canSeeFiles">
      <figure v-for="media in mediaInput.F" class="relative cursor-pointer min-h-32">
        <figcaption class="bg-gray-50/50 w-full">
          <div class="text-center mt-1 text-sm" v-text="media.filename"></div>
        </figcaption>
        <a :href="media.url"
          class="flex text-7xl bg-gray-300 cursor-pointer justify-center items-center h-44 max-w-full rounded-lg"
          :title="`Download ${media.filename}`" v-if="isFileType(media)">
          <span class="fa fa-file-download"></span>
        </a>
        <figcaption class="absolute bottom-2 bg-gray-50/50 w-full" v-if="!hideSave">
          <div class="text-center mt-1 text-xl" v-if="isFileType(media)">
            <span class="fa fa-trash text-red-500 cursor-pointer text-xl"
              @click.prevent="openDeleteMediaModal(media)"></span>
          </div>
        </figcaption>
      </figure>
      <p v-if="!mediaInput.F" class="text-gray-700">No records...</p>
    </div>

  </div>
  <DialogModal :show="preview !== null" @close="closePreviewModal" max-width="7xl">
    <template #title>
      <b v-text="preview.filename"></b>
    </template>

    <template #content>
      <img :src="preview.url" :alt="preview.filename" @error="event => fallbackToOldImage(event)">ß
    </template>
  </DialogModal>

  <DialogModal :show="deleting.media !== null" @close="closeDeletingMediaModal">
    <template #title>
      <b>Please confirm</b>
    </template>

    <template #content>
      You are about to delete <b v-text="deleting.media.filename"></b>.
      <br>Are you sure?
    </template>

    <template #footer>
      <SecondaryButton @click="closeDeletingMediaModal" class="mr-2" :disabled="deleting.loading">
        Cancel
      </SecondaryButton>
      <PrimaryButton @click="removeMedia(deleting.media.upload_id)" :disabled="deleting.loading">
        Confirm
      </PrimaryButton>
    </template>
  </DialogModal>
</template>

<style scoped></style>
